import {
  EQUIPE_FIELD_REQUIRED_MESSAGE,
  EQUIPE_OVERLAP_MESSAGE,
  temSobreposicao,
  validarPeriodoAlocacao,
} from "@/modules/recursos";
import type { EquipeObraProfissional, PapelObra, Profissional } from "@/modules/recursos";
import type { MembroEquipeSimulado } from "@/modules/recursos/simulacao";
import type { EquipeRow, RowErrors } from "@/shared/components/equipe-obra/equipe-obra.types";
import {
  ALOCACAO_JUSTIFICADA_TOAST,
  PROFISSIONAL_JUSTIFICADO_TOAST,
} from "./definir-equipe-obra.constants";
import { EQUIPE_UPDATED_TOAST } from "@/shared/components/equipe-obra/equipe-obra.constants";
import type { SituacoesConfirmadas } from "./hooks/use-salvar-com-verificacoes";
import type { NovaAlocacao } from "@/modules/recursos";

export const hasErrors = (errors: RowErrors) => Object.values(errors).some(Boolean);

/** Validação em tela de uma linha (datas, profissional e sobreposição) antes de qualquer requisição. */
export function rowErrorsFor(
  row: EquipeRow,
  rows: EquipeRow[],
  obra: { inicio: string; fim: string },
): { errors: RowErrors; missingProfissional: boolean } {
  const errors: RowErrors = { ...validarPeriodoAlocacao(row, obra) };
  const missingProfissional = !row.usuarioId;

  if (missingProfissional) {
    errors.usuario = EQUIPE_FIELD_REQUIRED_MESSAGE;
  } else if (
    temSobreposicao(
      row,
      rows.filter((other) => other.id !== row.id),
    )
  ) {
    errors.usuario = EQUIPE_OVERLAP_MESSAGE;
  }

  return { errors, missingProfissional };
}

/** Linha da tabela a partir de uma alocação já gravada (HU 2051). */
export function rowFromAlocacao(alocacao: EquipeObraProfissional): EquipeRow {
  return {
    id: alocacao.id,
    papelObraId: alocacao.papelObraId,
    usuarioId: alocacao.usuarioId,
    inicio: alocacao.inicio,
    fim: alocacao.fim,
    editing: false,
    isNew: false,
    backup: null,
    errors: {},
    gravada: true,
    papelNome: alocacao.papelNome,
    profissionalNome: alocacao.profissionalNome,
    base: { inicio: alocacao.inicio, fim: alocacao.fim },
    updatedAt: alocacao.updatedAt,
  };
}

/** Alocação gravada com período diferente do gravado: será enviada no Salvar. */
export function isRowAlterada(row: EquipeRow): boolean {
  return Boolean(
    row.gravada && row.base && (row.inicio !== row.base.inicio || row.fim !== row.base.fim),
  );
}

/**
 * Toast do Salvar (HU 2059): conflito confirmado é "Alocação confirmada"; só a
 * indisponibilidade confirmada é "Profissional adicionado"; sem justificativa, o padrão.
 */
export function mensagemSucesso(
  justificativa?: string,
  confirmadas?: SituacoesConfirmadas,
): string {
  if (!justificativa) return EQUIPE_UPDATED_TOAST;
  if (confirmadas?.indisponibilidade && !confirmadas.conflito) {
    return PROFISSIONAL_JUSTIFICADO_TOAST;
  }
  return ALOCACAO_JUSTIFICADA_TOAST;
}

/** Linhas com profissional e período válido: a nova alocação analisada no Calendário e no Salvar. */
export function novasAlocacoes(rows: EquipeRow[], papeis: Map<string, PapelObra>): NovaAlocacao[] {
  return rows.flatMap((row) =>
    row.usuarioId && row.inicio && row.fim && row.inicio <= row.fim
      ? [
          {
            id: row.id,
            usuarioId: row.usuarioId,
            papelObraId: row.papelObraId,
            papel: papeis.get(row.papelObraId)?.nome,
            inicio: row.inicio,
            fim: row.fim,
          },
        ]
      : [],
  );
}

/** A linha como membro da equipe simulada, com as datas pedidas (as gravadas ou as atuais). */
export function membroDaLinha(
  row: EquipeRow,
  datas: { inicio: string; fim: string },
  contexto: {
    profissionais: Profissional[];
    papeis: Map<string, PapelObra>;
    equipeDefinida: boolean;
  },
): MembroEquipeSimulado {
  const profissional = contexto.profissionais.find((item) => item.id === row.usuarioId);
  return {
    usuarioId: row.usuarioId ?? "",
    nome: profissional?.nome || profissional?.email || row.profissionalNome || "",
    time: profissional?.time || null,
    papelObraId: row.papelObraId,
    papel: contexto.papeis.get(row.papelObraId)?.nome ?? row.papelNome,
    inicio: datas.inicio,
    fim: datas.fim,
    ...(contexto.equipeDefinida && row.id ? { alocacaoId: row.id } : {}),
  };
}
